import { useState, useEffect } from "react"
import DOMPurify from 'dompurify'
import { flushSync } from "react-dom"
import { Switch, Form, Input, Mentions, InputNumber, Select } from "antd"
import TextArea from "antd/es/input/TextArea"
import { getAllWidgets } from "../../../utils/formsHelper"
import { useTranslation } from "react-i18next"

const NumberForm = ({ formValues, form, formWidgets, showPreviousDataFetch = false }) => {
    const { t } = useTranslation();
    let { formulaHtml, formula, computedNumber = false, isTableColumnComputation = false, isDateComputation = false, tableId } = form.getFieldsValue()
    const [formulaStr, setFormulaStr] = useState()
    const [numericWidgets, setNumericWidgets] = useState([])
    const [dateWidgets, setDateWidgets] = useState([])
    const [tableWidgets, setTableWidgets] = useState([])
    const [tableColumns, setTableColumns] = useState([])
    const [selectedWidgets, setSelectedWidgets] = useState([])

    const formulaWidgets = isDateComputation ? dateWidgets : numericWidgets

    // Initialize preview once from existing values; onChange drives further updates
    useEffect(() => {
        if (formulaHtml !== undefined && formulaHtml !== null) {
            setFormulaStr(formulaHtml)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (formulaWidgets.length > 0) {
            const widgetsPresent = []
            formulaWidgets.forEach(r => {
                if (formula && formula.includes('@' + r.label)) {
                    widgetsPresent.push(r)
                }
            })
            setSelectedWidgets(widgetsPresent)
        }
    }, [formula, formulaWidgets])

    useEffect(() => {
        if (computedNumber) {
            let formWidgetsList = formWidgets ? getAllWidgets(formWidgets) : []
            let filteredList = formWidgetsList.filter(f => f.formType === "numberForm")
            filteredList = filteredList.map(f => ({ ...f, value: f.label }))
            setNumericWidgets(filteredList)

        }
    }, [computedNumber, formWidgets])

    useEffect(() => {
        if (isDateComputation) {
            let formWidgetsList = formWidgets ? getAllWidgets(formWidgets) : []
            let filteredList = formWidgetsList.filter(f => f.formType === "dateForm")
            filteredList = filteredList.map(f => ({ ...f, value: f.label }))
            setDateWidgets(filteredList)
        }
    }, [isDateComputation, formWidgets])

    useEffect(() => {
        if (isTableColumnComputation) {
            let formWidgetsList = formWidgets ? getAllWidgets(formWidgets) : []
            let tableList = formWidgetsList.filter(f => f.formType === "tableForm").map(f => ({ ...f, value: f.id }))
            setTableWidgets(tableList)

        }
    }, [isTableColumnComputation, formValues, formWidgets])

    useEffect(() => {
        if (isTableColumnComputation && tableId) {
            let currentTable = tableWidgets.find(widget => widget.value === tableId);
            if (currentTable) {
                let cols = currentTable?.columns?.filter(col => col.inputType === "number").map(col => ({ ...col, value: col.id }))
                setTableColumns(cols)
            } else {
                setTableColumns([])
            }
        }
    }, [tableId, isTableColumnComputation, tableWidgets])

    const onChange = (value) => {
        if (!value) {
            setSelectedWidgets([])
            flushSync(() => {
                // Keep these as empty strings, never null: Mentions is still in
                // measuring mode right after "@" + backspace and slices its value.
                form.setFieldValue("formula", "")
                form.setFieldValue("formulaHtml", "")
                setFormulaStr("")
            })
            return
        }

        // Identify referenced widgets and build the HTML representation in one pass
        const widgetsPresent = []
        let string = value
        if (formulaWidgets.length > 0) {
            formulaWidgets.forEach((r) => {
                const pattern = '@' + r.label
                if (value.includes(pattern)) {
                    widgetsPresent.push(r)
                    try {
                        const replacement = `<span class="widget-inline-block" data-label="${r.label}" data-value="${r.id}" >${r.label}</span>`
                        string = string.replace(new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacement)
                    } catch (error) {
                        // no-op
                    }
                }
            })
        }
        // Flush updates synchronously and batch fields together to avoid previous-value lag
        flushSync(() => {
            setSelectedWidgets(widgetsPresent)
            // form.setFieldsValue({ formula: value, formulaHtml: string })
            form.setFieldValue("formula", value)
            setFormulaStr(string)
            form.setFieldValue("formulaHtml", string)
            setFormulaStr(string)
        })
    };
    // Removed secondary tempFormula processor to avoid race conditions and stale values

    const onSelect = (option) => {
        let temp = [...selectedWidgets]
        temp.push(option)
        setSelectedWidgets(temp)
    };

    const computationFlags = ["computedNumber", "isTableColumnComputation", "isDateComputation"]

    const handleComputationSwitch = (activeFlag) => (val) => {
        setSelectedWidgets([])
        setFormulaStr(null)
        form.setFieldValue("formula", null)
        form.setFieldValue("formulaHtml", null)
        form.setFieldValue("columnId", "")
        form.setFieldValue("tableId", "")
        if (val === true) {
            computationFlags.forEach((flag) => {
                if (flag !== activeFlag) form.setFieldValue(flag, false)
            })
        }
    }

    return (
        <>
            <Form.Item name="label" label={t("Label")}>
                <Input placeholder={t("Enter label")} />
            </Form.Item>

            <Form.Item name="placeholder" label={t("Placeholder")}>
                <Input placeholder={t("Enter placeholder")} />
            </Form.Item>

            <Form.Item name="tooltip" label={t("Tooltip")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            {
                formValues && formValues.tooltip &&
                <Form.Item label={t("Help Text")} name="helpText">
                    <TextArea rows={2} placeholder={t("Enter help text")} />
                </Form.Item>
            }

            <Form.Item name="mandatory" label={t("Mandatory")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="readOnly" label={t("Read Only")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="hideInitially" label={t("Hide Initially")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="computedNumber" label={t("Computed Number")} className="horizontal-form-item">
                <Switch size="small" onChange={handleComputationSwitch("computedNumber")} />
            </Form.Item>

            <Form.Item name="isTableColumnComputation" label={t("Table Computation")} className="horizontal-form-item">
                <Switch size="small" onChange={handleComputationSwitch("isTableColumnComputation")} />
            </Form.Item>

            <Form.Item name="isDateComputation" label={t("Date Computation")} className="horizontal-form-item">
                <Switch size="small" onChange={handleComputationSwitch("isDateComputation")} />
            </Form.Item>

            <Form.Item name="allowDecimal" label={t("Allow Decimal")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="allowNegative" label={t("Allow Negative")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>
            {
                showPreviousDataFetch &&
                <Form.Item name="enablePreviousDataFetch" label={t("Enable Previous Data Fetch")} className="horizontal-form-item">
                    <Switch size="small" />
                </Form.Item>
            }

            {
                formValues?.allowDecimal &&
                <Form.Item name="allowedDecimalPoint" label={t("Allowed Decimal Points")}>
                    <InputNumber placeholder={t("Enter allowed decimal points")} style={{ width: "100%" }} />
                </Form.Item>
            }

            {/* Min/Max value constraints */}
            <Form.Item
                name="minValue"
                label={t("Minimum")}
                dependencies={["maxValue"]}
                rules={[
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            const max = getFieldValue("maxValue")
                            if (value === undefined || value === null || value === "") return Promise.resolve()
                            if (max !== undefined && max !== null && max !== "" && Number(value) > Number(max)) {
                                return Promise.reject(new Error(t("Minimum cannot be greater than Maximum")))
                            }
                            return Promise.resolve()
                        }
                    })
                ]}
            >
                <InputNumber placeholder={t("Enter minimum value")} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
                name="maxValue"
                label={t("Maximum")}
                dependencies={["minValue"]}
                rules={[
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            const min = getFieldValue("minValue")
                            if (value === undefined || value === null || value === "") return Promise.resolve()
                            if (min !== undefined && min !== null && min !== "" && Number(value) < Number(min)) {
                                return Promise.reject(new Error(t("Maximum cannot be less than Minimum")))
                            }
                            return Promise.resolve()
                        }
                    })
                ]}
            >
                <InputNumber placeholder={t("Enter maximum value")} style={{ width: "100%" }} />
            </Form.Item>
            {
                (computedNumber || isDateComputation) ?
                    <>
                        <Form.Item name="formulaHtml">
                            <div style={{ marginBottom: 0 }}>
                                {t("Formula")}:
                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formulaStr || "") }} style={{ fontSize: 12 }}></div>
                            </div>
                        </Form.Item>

                        <Form.Item name="formula" getValueProps={(value) => ({ value: value ?? "" })}>
                            <Mentions
                                // disabled={computednumber ? false : true}
                                placeholder={t("Enter Calculation")}
                                // style={{ width: '100%', pointerEvents: !computednumber ? "none" : "unset" }}
                                onChange={onChange}
                                onSelect={onSelect}
                                autoSize
                                className="formula-field"
                                options={formulaWidgets}
                            />
                        </Form.Item>
                    </>
                    : null
            }

            {
                formValues?.isTableColumnComputation ?
                    <>

                        <Form.Item name="tableId" label={t("Select Table")}>
                            <Select allowClear options={tableWidgets} onChange={(val) => {
                                let currentTable = tableWidgets.find(widget => widget.value === val);
                                if (currentTable) {
                                    let cols = currentTable?.columns?.filter(col => col.inputType === "number").map(col => ({ ...col, value: col.id }))
                                    setTableColumns(cols)
                                } else {
                                    setTableColumns([])
                                }
                            }} />
                        </Form.Item>
                        <Form.Item name="columnId" label={t("Select Column")}>
                            <Select options={tableColumns} />
                        </Form.Item>

                    </>
                    :
                    null
            }

            <Form.Item name="unit" label={t("Unit")} help={t("Unit will be displayed next to the input in field")} style={{ paddingBottom: "1rem" }}>
                <Input placeholder={t("Enter unit here")} />
            </Form.Item>
        </>
    )
}

export default NumberForm